import * as React from 'react'

import { Box, type LabeledInputProps, Text } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { reaction } from 'mobx'

import { observer } from 'mobx-react'

import InputText from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

interface Props {
  form: Form
  user: User
  employeeExists: boolean
  isCurrentUser: boolean
  canEditEmployeeEmail?: boolean
  readonly?: boolean
  autofocus?: boolean
  mandatory?: boolean
  secondaryLabel?: LabeledInputProps['secondaryLabel']
}

const Email = (props: Props) => {
  const {
    user,
    form,
    readonly,
    employeeExists,
    isCurrentUser,
    canEditEmployeeEmail,
    secondaryLabel,
    mandatory,
    autofocus,
  } = props

  const isReadOnly = React.useMemo(() => {
    if (user.isNew) return false

    const canNotEdit = employeeExists
      ? !Boolean(canEditEmployeeEmail)
      : Boolean(!isCurrentUser && user.attributes.joined)

    return readonly || canNotEdit
  }, [user, readonly, employeeExists, canEditEmployeeEmail, isCurrentUser])

  React.useEffect(() => {
    const handler = reaction(
      () => user.attributes.unconfirmed_email,
      () => {
        const email = form.get('email')
        email.set(user.loginEmail || '')
        email.clean()

        const uncomfirmedEmail = form.get('unconfirmed_email')
        uncomfirmedEmail.set(user.attributes.unconfirmed_email)
        uncomfirmedEmail.clean()
      },
      {
        fireImmediately: true,
      }
    )

    return () => {
      if (handler) {
        handler()
      }
    }
  }, [user, form])

  const renderResendInfo = () => {
    if (user.isNew) return null

    const userIsInvited = user.attributes.invited
    const equalEmails = user.attributes.unconfirmed_email === user.attributes.email
    const unconfirmedEmail = user.attributes.unconfirmed_email

    if (user.isNew || !userIsInvited || !unconfirmedEmail || equalEmails) {
      return null
    }

    return (
      <>
        <Text size="200">
          {i18n.t('employee_form.unconfirmed_description', {
            email: unconfirmedEmail,
          })}
        </Text>
      </>
    )
  }

  const bangText: boolean | undefined = isReadOnly || mandatory

  return (
    <Box gap="s8">
      <InputText
        readonly={isReadOnly}
        field={form.get('email')}
        value={form.get('email').value}
        label={i18n.t('employee_form.email')}
        mandatory={bangText}
        autofocus={autofocus}
        secondaryLabel={secondaryLabel}
      />
      {renderResendInfo()}
    </Box>
  )
}

export default observer(Email)
